import { useEffect, useRef, useState } from "react";

type Props = {
  timezone: string;
  targetHour: number;
  targetMinute: number;
  label?: string;
  variant?: "pill" | "inline";
  /** Вызовется один раз на каждую пройденную цель (перекат окна в H:MM),
   * после чего таймер сам берёт следующую цель. OF-PKG1A (04.10.2026):
   * раньше условие «остаток <= 0» было недостижимо — nextTargetMs() сразу
   * после H:MM возвращает завтрашнюю цель, остаток всегда > 0, колбэк не
   * вызывался никогда (OF9). Теперь цель хранится в ref и сравнивается с
   * Date.now() на каждом тике. */
  onReached?: () => void;
};

export default function Countdown({
  timezone,
  targetHour,
  targetMinute,
  label,
  variant = "inline",
  onReached,
}: Props) {
  // Текущая цель (unix ms). Пройдена → onReached один раз, затем новая цель.
  const targetRef = useRef(nextTargetMs(timezone, targetHour, targetMinute));
  const [msLeft, setMsLeft] = useState(() => targetRef.current - Date.now());
  // Колбэк через ref: родитель передаёт новую функцию на каждом рендере, а
  // перезапуск интервала из-за этого не нужен.
  const onReachedRef = useRef(onReached);
  onReachedRef.current = onReached;

  useEffect(() => {
    // при смене входных параметров — новая цель
    targetRef.current = nextTargetMs(timezone, targetHour, targetMinute);
    setMsLeft(targetRef.current - Date.now());
  }, [timezone, targetHour, targetMinute]);

  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      if (now >= targetRef.current) {
        targetRef.current = nextTargetMs(timezone, targetHour, targetMinute);
        // даём кадр на перерисовку, потом дергаем колбэк
        Promise.resolve().then(() => onReachedRef.current?.());
      }
      setMsLeft(targetRef.current - now);
    }, 1000);
    return () => clearInterval(id);
  }, [timezone, targetHour, targetMinute]);

  const { hh, mm, ss } = splitHMS(Math.max(0, Math.floor(msLeft / 1000)));

  const content = (
    <span className="tabular-nums">
      {hh}:{mm}:{ss}
    </span>
  );

  if (variant === "pill") {
    return (
      <div className="inline-flex items-center gap-2 text-sm">
        {label && <span className="opacity-70">{label}</span>}
        <span className="px-3 py-1 rounded-full border border-gray-200 bg-white shadow-sm">
          {content}
        </span>
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-1 text-sm">
      {label && <span className="opacity-70">{label}</span>}
      {content}
    </div>
  );
}

/* ===== helpers (таймзона надёжно) ===== */

function splitHMS(totalSec: number) {
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  return {
    hh: String(h).padStart(2, "0"),
    mm: String(m).padStart(2, "0"),
    ss: String(s).padStart(2, "0"),
  };
}

function ymdInTZ(timezone: string, base = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: timezone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(base);
  const y = Number(parts.find((p) => p.type === "year")?.value);
  const m = Number(parts.find((p) => p.type === "month")?.value);
  const d = Number(parts.find((p) => p.type === "day")?.value);
  return { y, m, d };
}

function tzOffsetMinutesAt(
  timezone: string,
  y: number,
  m: number,
  d: number,
  hh: number,
  mm: number
) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    timeZoneName: "shortOffset",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  const parts = dtf.formatToParts(new Date(Date.UTC(y, m - 1, d, hh, mm, 0)));
  const name = parts.find((p) => p.type === "timeZoneName")?.value || "GMT+00";
  const sign = name.includes("-") ? -1 : 1;
  const m2 = name.split("GMT")[1].replace("+", "").replace("-", "");
  const [hOff, minOff = "0"] = m2.split(":");
  return sign * (parseInt(hOff || "0", 10) * 60 + parseInt(minOff || "0", 10));
}

function utcMsForTZLocal(
  timezone: string,
  y: number,
  m: number,
  d: number,
  hh: number,
  mm: number
) {
  const baseUTC = Date.UTC(y, m - 1, d, hh, mm, 0);
  const offMin = tzOffsetMinutesAt(timezone, y, m, d, hh, mm);
  return baseUTC - offMin * 60_000;
}

/** unix ms следующей точки "сегодня/завтра в H:MM" по TZ */
function nextTargetMs(timezone: string, hour: number, minute: number) {
  const now = new Date();
  const { y, m, d } = ymdInTZ(timezone, now);
  let target = utcMsForTZLocal(timezone, y, m, d, hour, minute);
  if (now.getTime() >= target) {
    // уже прошли — берём завтра
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + 1);
    target = utcMsForTZLocal(
      timezone,
      dt.getUTCFullYear(),
      dt.getUTCMonth() + 1,
      dt.getUTCDate(),
      hour,
      minute
    );
  }
  return target;
}
